import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    X, Copy, UserCheck, Loader2, User, BarChart3, Check, Award, Info
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from '@mui/material';

const IndividualScheduleModal = ({
    isOpen,
    onClose,
    candidate,
    jobDescription,
    onCreateInvite,
    onSkillAnalysis,
    selectedSkills,
    setSelectedSkills,
    scheduleResult,
    isCreatingInvite,
    setSnackbar
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    if (!isOpen || !candidate) return null;

    const handleClose = () => {
        onClose();
        setSelectedSkills([]);
    };

    const getMatchColor = (percentage) => {
        if (percentage >= 75) return 'bg-green-100 text-green-800 border-green-200';
        if (percentage >= 50) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
        return 'bg-red-100 text-red-800 border-red-200';
    };

    return (
        <div className="fixed inset-0 bg-gradient-to-br from-black/40 via-gray-900/50 to-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-3">
            <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[95vh] overflow-hidden shadow-2xl">
                {/* Header */}
                <div className="sticky top-0 border-b border-gray-200 p-4 rounded-t-2xl" style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}>
                    <div className="flex justify-between items-center">
                        <div>
                            <h2 className="text-xl font-bold text-white mb-0.5">Schedule Interview</h2>
                            <p className="text-blue-100 text-sm">
                                {jobDescription?.title}
                            </p>
                        </div>
                        <button
                            onClick={handleClose}
                            className="p-2 hover:bg-white/20 rounded-lg transition-all duration-200 group"
                        >
                            <X size={20} className="text-white group-hover:text-blue-100" />
                        </button>
                    </div>
                </div>

                <div className="overflow-y-auto max-h-[calc(95vh-160px)] p-4">
                    {scheduleResult ? (
                        // Results view
                        <div className="space-y-4">
                            {scheduleResult.success ? (
                                <>
                                    {/* Success Banner */}
                                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-4">
                                        <div className="flex items-center gap-3">
                                            <div className="bg-green-100 rounded-full p-2">
                                                <Check size={20} className="text-green-600" />
                                            </div>
                                            <div>
                                                <h3 className="text-base font-semibold text-green-800 mb-0.5">
                                                    Interview Invite Created Successfully!
                                                </h3>
                                                <p className="text-sm text-green-700">
                                                    Invite created for <span className="font-semibold">{scheduleResult.candidateName}</span> - {jobDescription?.title}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Invite Details */}
                                    <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                                        <div className="flex items-center gap-2 mb-4">
                                            <div className="bg-green-100 rounded-full p-1.5">
                                                <Check size={16} className="text-green-600" />
                                            </div>
                                            <h4 className="text-base font-semibold text-gray-900">Invite Details</h4>
                                        </div>

                                        <div className="bg-gradient-to-r from-gray-50 to-green-50 border border-gray-200 rounded-lg p-3">
                                            <div className="flex items-center gap-2 mb-2">
                                                <User size={14} className="text-green-600" />
                                                <span className="text-sm font-medium text-gray-900">{scheduleResult.candidateName}</span>
                                                {scheduleResult.skillsUsed && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-800 border border-purple-200">
                                                        Skills Configured
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <input
                                                    type="text"
                                                    value={scheduleResult.interviewUrl}
                                                    readOnly
                                                    className="text-xs bg-white border border-gray-300 rounded-md px-2 py-1 flex-1 text-gray-600"
                                                />
                                                <button
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(scheduleResult.interviewUrl);
                                                        setSnackbar({
                                                            open: true,
                                                            message: 'Interview URL copied to clipboard',
                                                            severity: 'success'
                                                        });
                                                    }}
                                                    className="p-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
                                                >
                                                    <Copy size={14} />
                                                </button>
                                            </div>
                                        </div>

                                        {selectedSkills.length > 0 && (
                                            <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                                                <p className="text-xs font-semibold text-purple-800 mb-2">Skills included in this invite:</p>
                                                <div className="flex flex-wrap gap-1.5">
                                                    {selectedSkills.map((skill, index) => (
                                                        <span
                                                            key={index}
                                                            className="text-xs px-2 py-0.5 text-white rounded-md shadow-sm"
                                                            style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}
                                                        >
                                                            {skill}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </>
                            ) : (
                                // Error view
                                <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
                                    <div className="flex items-center gap-3">
                                        <div className="bg-red-100 rounded-full p-2">
                                            <X size={20} className="text-red-600" />
                                        </div>
                                        <div>
                                            <h3 className="text-base font-semibold text-red-800 mb-0.5">
                                                Failed to Create Invite
                                            </h3>
                                            <p className="text-sm text-red-700">
                                                {scheduleResult.error}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        // Selection view
                        <div className="space-y-4">
                            {/* Candidate Info Card */}
                            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
                                <div className="flex items-start gap-3">
                                    <div className="bg-blue-100 rounded-full p-2">
                                        <User size={20} className="text-blue-600" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 
                                            className="font-semibold text-blue-900 text-base mb-1 cursor-pointer hover:text-blue-700 transition-colors"
                                            onClick={() => navigate(`/candidates/${candidate.candidate_id}`)}
                                        >
                                            {candidate.candidate_name}
                                        </h3>
                                        <div className="flex items-center gap-2 mb-2">
                                            <Tooltip
                                                title={
                                                    <div className="p-2 max-w-xs">
                                                        <div className="flex items-center gap-2 mb-2 pb-1 border-b border-gray-200">
                                                            <Award size={14} className="text-yellow-600" />
                                                            <span className="font-bold text-sm text-gray-800">{t('common.matchedSkills')}</span>
                                                        </div>
                                                        {candidate.matched_skills && candidate.matched_skills.length > 0 ? (
                                                            <div className="flex flex-wrap gap-1.5 max-h-[120px] overflow-y-auto pr-1 custom-scrollbar">
                                                                {candidate.matched_skills.map((skill, i) => {
                                                                    const colors = [
                                                                        "bg-sky-50 text-sky-700 border-sky-100",
                                                                        "bg-emerald-50 text-emerald-700 border-emerald-100",
                                                                        "bg-indigo-50 text-indigo-700 border-indigo-100"
                                                                    ];
                                                                    const colorClass = colors[i % colors.length];
                                                                    return (
                                                                        <span key={i} className={`px-2 py-0.5 rounded text-[10px] font-medium border whitespace-nowrap ${colorClass}`}>
                                                                            {skill}
                                                                        </span>
                                                                    );
                                                                })}
                                                            </div>
                                                        ) : (
                                                            <p className="text-xs italic text-gray-500">{t('common.noMatchedSkills')}</p>
                                                        )}
                                                    </div>
                                                }
                                                slotProps={{
                                                    tooltip: {
                                                        sx: {
                                                            bgcolor: 'white',
                                                            color: 'rgba(0, 0, 0, 0.87)',
                                                            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                                                            border: '1px solid #e2e8f0',
                                                            '& .MuiTooltip-arrow': {
                                                                color: 'white',
                                                                '&::before': {
                                                                    border: '1px solid #e2e8f0',
                                                                    backgroundColor: 'white',
                                                                },
                                                            },
                                                        },
                                                    },
                                                }}
                                                arrow
                                                placement="top"
                                                enterTouchDelay={0}
                                            >
                                                <span className={`px-2 py-1 rounded-full text-xs font-semibold border cursor-pointer ${getMatchColor(candidate.match_percentage)}`}>
                                                    <Award size={12} className="inline mr-1" />
                                                    {candidate.match_percentage}% Match
                                                </span>
                                            </Tooltip>
                                            <span className="text-xs text-gray-600">
                                                Resume ID: #{candidate.resume_id}
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div
                                                className={`h-2 rounded-full ${candidate.match_percentage >= 75
                                                    ? 'bg-green-500'
                                                    : candidate.match_percentage >= 50
                                                        ? 'bg-yellow-500'
                                                        : 'bg-red-500'
                                                    }`}
                                                style={{ width: `${candidate.match_percentage}%` }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Skill Analysis Section */}
                            <div className="bg-white border border-gray-200 rounded-xl p-4">
                                <div className="flex items-center gap-2 mb-3">
                                    <div className="rounded-full p-2" style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}>
                                        <BarChart3 size={16} className="text-white" />
                                    </div>
                                    <h4 className="text-base font-semibold text-gray-900">Skill Configuration</h4>
                                </div>
                                <p className="text-sm text-gray-600 mb-3">
                                    Analyze candidate skills to customize the interview based on their expertise
                                </p>
                                <button
                                    onClick={onSkillAnalysis}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm rounded-lg transition-all duration-200 shadow-sm text-white"
                                    style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}
                                >
                                    <BarChart3 size={16} />
                                    <span className="font-medium">Analyze Skills</span>
                                </button>
                            </div>

                            {/* Selected Skills Display */}
                            {selectedSkills.length > 0 && (
                                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-xl p-4">
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="rounded-full p-1.5" style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}>
                                            <Check size={14} className="text-white" />
                                        </div>
                                        <h4 className="text-sm font-semibold text-purple-900">Selected Skills ({selectedSkills.length})</h4>
                                    </div>
                                    <div className="flex flex-wrap gap-1.5 mb-3">
                                        {selectedSkills.map((skill, index) => (
                                            <span
                                                key={index}
                                                className="text-xs px-2 py-1 text-white rounded-md shadow-sm"
                                                style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}
                                            >
                                                {skill}
                                            </span>
                                        ))}
                                    </div>
                                    <button
                                        onClick={() => setSelectedSkills([])}
                                        className="text-xs text-purple-600 hover:text-purple-800 hover:underline"
                                    >
                                        Clear Skills
                                    </button>
                                </div>
                            )}

                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 bg-gradient-to-r from-gray-50 to-slate-50 border-t border-gray-200 p-4 rounded-b-2xl">
                    {scheduleResult ? (
                        <div className="flex justify-end">
                            <button
                                onClick={handleClose}
                                className="px-4 py-2 text-sm bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    ) : (
                        <div className="flex items-center justify-between">
                            <div className="text-sm text-gray-600">
                                {selectedSkills.length > 0 ? (
                                    <span className="text-purple-600 font-medium">
                                        {selectedSkills.length} skill{selectedSkills.length !== 1 ? 's' : ''} configured
                                    </span>
                                ) : (
                                    <span className="text-gray-500">No skills configured</span>
                                )}
                            </div>
                            <div className="flex gap-3">
                                <button
                                    onClick={handleClose}
                                    className="px-4 py-2 text-sm border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={onCreateInvite}
                                    disabled={isCreatingInvite}
                                    className="px-4 py-2 text-sm text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center gap-2 shadow-sm"
                                    style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}
                                >
                                    {isCreatingInvite ? (
                                        <>
                                            <Loader2 className="animate-spin" size={16} />
                                            Creating Invite...
                                        </>
                                    ) : (
                                        <>
                                            <UserCheck size={16} />
                                            {selectedSkills.length > 0 ? 'Create Invite with Skills' : 'Create Invite'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default IndividualScheduleModal;